import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useContext, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';

import { useNavigate } from 'react-router-dom';
import Accordion from '../../components/Accordion';
import AccordionBodyItem from '../../components/Accordion/AccordionBodyItem';
import AvatarGroup, {
  AvatarGroupSize,
  AvatarGroupStyle,
} from '../../components/Avatar/AvatarGroup';
import Button from '../../components/Button';
import DefaultCard from '../../components/DefaultCard';
import IconButton from '../../components/IconButton';
import Label from '../../components/Label/Label';
import Popover from '../../components/Popover';
import useModal from '../../hooks/useModal';
import { AuthStore } from '../../providers/auth';
import {
  EnrollmentProvider,
  EnrollmentStore,
} from '../../providers/enrollment';
import { FamilyMembersProvider } from '../../providers/familyMembers';
import {
  addOrRemoveDaysToGivenDate,
  formatDate,
  formatPrice,
  openExternalLink,
  stopVideo,
} from '../../utils/utils';
import BenefitModalCobra from '../widgets/benefit/BenefitModalCobra';
import BenefitOptionsAndCostsCobra from '../widgets/BenefitOptionsAndCostsModal/BenefitOptionsAndCostsCobraModal';
import DentalBenefitsModal from '../widgets/DentalBenefitsModal/DentalBenefitsModal';
import DependantFsaModal from '../widgets/dependantFSA/DependantFsaModal';
import EnrollmentSuccess from '../widgets/EnrollmentSuccessPage/EnrollmentSuccessCobra';
import ViewFamilyMembersModal from '../widgets/healthBenefits/ViewFamilyMembersModal';
import HealthFsaModal from '../widgets/healthFSA/HealthFsaModal';
import MedicalBenefitsModal from '../widgets/MedicalBenefitsModal';
import PopoverWithAlerts from '../widgets/PopoverAlerts/PopoverAlerts';
import VideoEmbedModal from '../widgets/VideoEmbedModal/VideoEmbedModal';
import VisionBenefitsModal from '../widgets/VisionBenefitsModal/VisionBenefitsModal';

const alertsData: any[] = [
  {
    alertDescription:
      'Make your initial COBRA payment for all your benefits elections to go into effect.',
  },
];

const separateStringAndMakeFirstLetterUppercase = (str: string) => {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getCardIcon = (type: String) => {
  switch (type) {
    case 'heart-pulse':
      return light('heart-pulse');
    case 'tooth':
      return light('tooth');
    case 'glasses':
      return light('glasses');
    case 'face-thermometer':
      return light('face-thermometer');
    case 'family':
      return light('family');
    case 'bed-pulse':
      return light('bed-pulse');
    case 'hospitals':
      return light('hospitals');
    case 'gavel':
      return light('gavel');
    default:
      return light('image-slash');
  }
};

const getPlanCostStyle = (type: string) => {
  switch (type) {
    case 'owed':
      return 'default';
    case 'credit':
      return 'green';
    default:
      return 'disabled';
  }
};

const getButtonStyle = (
  type: string,
  alertInfo: any[],
  proceedBtnFunc: any
) => {
  const alertBody = (
    <Col>
      <Row className="pb-7 m-0 fw-normal">
        {alertInfo && alertInfo[0].title}
      </Row>
      <Row className="pb-7 m-0 fw-light lh-xs">
        {alertInfo && alertInfo[0].description}
      </Row>
      <Col className="m-0 d-flex justify-content-end">
        {alertInfo && alertInfo[0].ctaLabel ? (
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              document.body.click();
              proceedBtnFunc();
            }}
          >
            {alertInfo && alertInfo[0].ctaLabel}
          </Button>
        ) : (
          ''
        )}
      </Col>
    </Col>
  );

  switch (type) {
    case 'alert':
      return (
        <Popover placement="top-end" popoverBody={alertBody}>
          <IconButton
            variant="icon-alert"
            icon={light('circle-exclamation')}
            size={'sm'}
          />
        </Popover>
      );
    case 'button':
      return (
        <IconButton
          variant="icon-emphasized"
          icon={light('file-arrow-down')}
          size={'sm'}
          onClick={() =>
            openExternalLink('https://mag.wcoomd.org/uploads/2018/05/blank.pdf')
          }
        ></IconButton>
      );
    default:
      return;
  }
};

const getAlertButton = (alertInfo: any[], proceedBtnFunc: any) => {
  const alertBody = (
    <Col>
      <Row className="pb-7 m-0 fw-normal pt-2">
        {alertInfo && alertInfo[0].title}
      </Row>
      <Row className="pb-2 m-0 fw-light lh-xs">
        {alertInfo && alertInfo[0].description}
      </Row>
      <Col className="m-0 d-flex justify-content-end">
        {alertInfo && alertInfo[0].ctaLabel ? (
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              document.body.click();
              proceedBtnFunc();
            }}
          >
            {alertInfo && alertInfo[0].ctaLabel}
          </Button>
        ) : (
          ''
        )}
      </Col>
    </Col>
  );

  return (
    <Popover placement="top-end" popoverBody={alertBody}>
      <IconButton
        variant="icon-alert"
        icon={light('circle-exclamation')}
        size={'sm'}
        extLabel="Pending"
        style={{ color: '#f93b1d' }}
      />
    </Popover>
  );
};

const getInfoButton = () => {
  return (
    <IconButton
      variant="icon-emphasized"
      icon={light('file-arrow-down')}
      size={'sm'}
      onClick={() =>
        openExternalLink('https://mag.wcoomd.org/uploads/2018/05/blank.pdf')
      }
    ></IconButton>
  );
};

function CobraBenefitsEnrollmentView({ setSubmitted }: any) {
  const navigate = useNavigate();
  const [,] = useContext(AuthStore) || [];
  const {
    updatedCost,
    activeHealthBenefits,
    activeSpendingAccounts,
    medicalSelectedProfiles,
    dentalSelectedProfiles,
    visionSelectedProfiles,
    familyMembers,
    addFamilyMember,
  } = useContext(EnrollmentStore) || {};

  const [shouldShowCurrentValue, setShowCurrentValue] =
    useState<boolean>(false);

  const [medicalSelectedPlanRadio, setMedicalSelectedPlanRadio] = useState<
    string | number
  >(1);

  const healthBenefits = useMemo(() => {
    const plans = activeHealthBenefits.filter(
      (e: any) => e.type !== 'health-savings'
    );

    return plans;
  }, [activeHealthBenefits]);

  const [showBenefitOptionsAndCostsCobra, handleShowBenefitOptionsAndCostsCobra, handleCloseBenefitOptionsAndCostsCobra] =
    useModal();

  const [showBenefitModalCobra, handleShowBenefitModalCobra, handleCloseBenefitModalCobra] =
    useModal();
  const [showBenefitModal, handleShowBenefitModal, handleCloseBenefitModal] =
    useModal();
  const [
    showViewFamilyMemberModal,
    handleShowViewFamilyMemberModal,
    handleCloseViewFamilyMemberModal,
  ] = useModal();
  const [
    showHealthFSAModal,
    handleShowHealthFSAModal,
    handleCloseHealthFSAModal,
  ] = useModal();
  const [
    showDependedFSAModal,
    handleShowDependedFSAModal,
    handleCloseDependedFSAModal,
  ] = useModal();
  const [
    showMedicalBenefitsModal,
    handleShowMedicalBenefitsModal,
    handleCloseMedicalBenefitModal,
  ] = useModal();

  const [
    showDentalBenefitsModal,
    handleShowDentalBenefitsModal,
    handleCloseDentalBenefitsModal,
  ] = useModal();

  const [
    showVisionBenefitsModal,
    handleShowVisionBenefitsModal,
    handleCloseVisionBenefitsModal,
  ] = useModal();

  const getButtonModal = (type: string) => {
    switch (type) {
      case 'health-care':
        return handleShowHealthFSAModal;
      case 'dependant-care':
        return handleShowDependedFSAModal;
      case 'medical':
        return handleShowMedicalBenefitsModal;
      case 'dental':
        return handleShowDentalBenefitsModal;
      case 'vision':
        return handleShowVisionBenefitsModal;
    }
  };

  const getActiveProfilesByType = (type: string) => {
    switch (type) {
      case 'medical':
        return medicalSelectedProfiles.flat();
      case 'vision':
        return visionSelectedProfiles.flat();
      case 'dental':
        return dentalSelectedProfiles.flat();
    }
  };

  let shouldHaveHealthBenefitsWarnings = false;
  healthBenefits.forEach((b: any) => {
    if (!b['plan']['id'].includes('Coverage'))
      shouldHaveHealthBenefitsWarnings = true;
  });

  let currentAlerts: Array<any> = [];
  if (shouldHaveHealthBenefitsWarnings) {
    currentAlerts = [...alertsData];
  }

  const [
    showVideoEmbedModal,
    handleShowVideoEmbedModal,
    handleCloseVideoEmbedModal,
  ] = useModal();

  return (
    <Container className="cnd-custom-container-large mb-12 px-lg-9 px-xl-0">
      <Row>
        <Col className="mt-10 col-12 col-lg-8">
          <DefaultCard
            className="h-100"
            variant="blue-motif"
            leftHeading={'Benefits Sneak Peek'}
            leftSubheading={`Let's take a quick look into your benefits`}
            button={
              <Button
                className="text-nowrap"
                variant="primary-shadow"
                size="sm"
                rightIcon={light('play')}
                onClick={handleShowVideoEmbedModal}
              >
                Watch Now
              </Button>
            }
          />
          <VideoEmbedModal
            showVideoEmbedModal={showVideoEmbedModal}
            handleCloseVideoEmbedModal={handleCloseVideoEmbedModal}
            stopVideo={stopVideo}
            title={'Benefits Sneak Peek'}
            embeddedNode={
              <div className={'d-flex justify-content-center'}>
                <iframe
                  src={`${process.env.PUBLIC_URL}/${'jellyVision.mp4'}`}
                  title={'rollover'}
                  width={620}
                  height={450}
                ></iframe>
              </div>
            }
          />
        </Col>
        <Col className="col-6 col-lg-4 mt-10">
          <DefaultCard
            variant="white"
            className="h-100"
            leftHeading={formatPrice(updatedCost)}
            leftSubheading={`Monthly payment`}
            overlineLabel={<Label>Total</Label>}
            bottomLine={
              <Label className={'text-nowrap p-0'} l>
                Effective Date:{' '}
                {formatDate(addOrRemoveDaysToGivenDate(new Date(), 30))}
              </Label>
            }
          />
        </Col>
      </Row>
      <Row className="mt-12">
        <div className="w-100 d-flex justify-content-between align-items-center">
          <div><h4>Enroll in COBRA coverage</h4></div>
 
          <div className="d-flex align-items-center justify-content-end">
            <div className={'d-flex align-items-center'}>
              <IconButton
                variant="secondary-shadow"
                icon={light('circle-dollar')}
                onMouseDown={handleShowBenefitOptionsAndCostsCobra}
                style={{ backgroundColor: '#9DB4C04D', boxShadow: 'none' }}
              />
              <Label
                className="d-none d-lg-flex px-4"
                style={{ color: '#54626A', cursor: 'pointer' }}
                onMouseDown={handleShowBenefitOptionsAndCostsCobra}
                >
               Benefit Options & Costs
              </Label>
          <BenefitOptionsAndCostsCobra
              showBenefitOptionsAndCostsCobra={showBenefitOptionsAndCostsCobra}
              handleCloseBenefitOptionsAndCostsCobra={handleCloseBenefitOptionsAndCostsCobra}
            />
            </div>
            <IconButton
              variant="secondary-shadow"
              icon={light('circle-info')}
              onMouseDown={handleShowBenefitModalCobra}
              style={{ backgroundColor: '#9DB4C04D', boxShadow: 'none' }}
            />
            <Label
              className="d-none d-lg-flex px-4"
              style={{ color: '#54626A', cursor: 'pointer' }}
              onMouseDown={handleShowBenefitModalCobra}
            >
              Benefits Info
            </Label>
            <BenefitModalCobra
              showBenefitModalCobra={showBenefitModalCobra}
              handleCloseBenefitModalCobra={handleCloseBenefitModalCobra}
            />
          </div>
        </div>
        
      </Row>
      <Row className="mt-10 w-100">      
        <div className="mx-5 custom-blockquote primary default lh-xs container mb-10 col-lg-12 col-12">
                <div className="mb-0 text-ligther-grey">
                  <span className="fw-bold">
                    IMPORTANT!: </span>
                    <span> Once you submit your elections, you won't able to make any additional changes.
                  </span>
                </div>
          </div>
          </Row>
      <Row className="mt-10">

        <Accordion
          title={'Health Benefits'}
          className={'highlight-open'}
          infoIcon={
            shouldHaveHealthBenefitsWarnings
              ? light('circle-exclamation')
              : undefined
          }
        >
          <Container className="custom-accordion-demo heading">
            <Row className="body-rows">
              <Col className="title-container col-lg-1">&nbsp;</Col>
              <Col className="avatars-col col-lg-3">&nbsp;</Col>
              <Col className="avatars-col col-lg-2">&nbsp;</Col>
              <Col className="d-flex p-lg-0 col-lg-2">
                <Col className="price-col me-6 me-lg-0">
                  <span className="text-light-grey-normal text-fs-6 fw-light text-nowrap">
                    {shouldShowCurrentValue ? 'Current Cost' : ''}
                  </span>
                </Col>
                <Col className="price-col">
                  <span className="text-light-grey-normal text-fs-6 fw-light text-nowrap">
                    Pay-Per-Period
                  </span>
                </Col>
              </Col>
            </Row>
          </Container>
          {healthBenefits.map((benefit: any, index: number) => {
            return (
              <AccordionBodyItem
                key={index}
                infoIcon={getCardIcon(
                  benefit['generic'] && benefit['generic']['icon']
                )}
                title={benefit['generic']['title']}
                subtitle={benefit['plan'] ? benefit['plan']['title'] : 'Waived'}
                infoButton={
                  !benefit['plan']['id'].includes('Coverage') &&
                  getAlertButton(
                    benefit['alerts'],
                    getButtonModal(benefit['type'])
                  )
                }
                price={
                  benefit['plan']
                    ? formatPrice(Math.abs(benefit['plan']['cost']))
                    : '$0.00'
                }
                originalPrice={
                  benefit['plan']
                    ? formatPrice(Math.abs(benefit['plan']['originalCost']))
                    : '$0.00'
                }
                isOriginalPriceShown={shouldShowCurrentValue}
                priceStyleType={getPlanCostStyle(
                  benefit['plan']['cost'] === 0
                    ? ''
                    : benefit['plan'] && benefit['plan']['costType']
                )}
                originalPriceStyleType={getPlanCostStyle(
                  benefit['plan']['cost'] === 0
                    ? ''
                    : benefit['plan']['originalCost'] < 0
                    ? 'credit'
                    : benefit['plan'] && benefit['plan']['costType']
                )}
                
                changeButton={
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={getButtonModal(benefit['type'])}
                  >
                    Change
                  </Button>
                }
                isHint={benefit['type'] === 'medical'}
                avatarGroup={
                  benefit['generic']['familyMembers'] && (
                    <AvatarGroup
                      key={index}
                      avatars={getActiveProfilesByType(benefit['type'])}
                      groupSize={AvatarGroupSize.MINIMIZED}
                      groupStyle={AvatarGroupStyle.STACKED}
                      hasTooltip
                    />
                  )
                }
              />
            );
          })}
          <MedicalBenefitsModal
            radioButtonValue={medicalSelectedPlanRadio}
            setRadioButtonValue={setMedicalSelectedPlanRadio}
            showMedicalBenefitModal={showMedicalBenefitsModal}
            handleCloseMedicalBenefitModal={handleCloseMedicalBenefitModal}
          />
          <DentalBenefitsModal
            showDentalBenefitsModal={showDentalBenefitsModal}
            handleCloseDentalBenefitsModal={handleCloseDentalBenefitsModal}
          />
          <VisionBenefitsModal
            showVisionBenefitsModal={showVisionBenefitsModal}
            handleCloseVisionBenefitsModal={handleCloseVisionBenefitsModal}
          />
        </Accordion>
      </Row>
       
      <section className="mt-10 d-flex flex-column flex-lg-row justify-content-center">
        <Button
          className="text-nowrap order-2 order-lg-1 mt-7 mt-lg-0 me-lg-7"
          variant="primary-outline"
        >
          Cancel
        </Button>
        <Button
          onClick={() => setSubmitted(true)}
          className="text-nowrap order-1 order-lg-2"
          variant="primary"
        >
          Submit
        </Button>
      </section>
    </Container>
  );
}

function CobraBenefitsEnrollment() {
  const [submmited, setSubmitted] = useState(false);

  return (
    <EnrollmentProvider>
      <FamilyMembersProvider>
        {!submmited ? (
          <CobraBenefitsEnrollmentView setSubmitted={setSubmitted} />
        ) : (
          <EnrollmentSuccess setSubmitted={setSubmitted} />
        )}
      </FamilyMembersProvider>
    </EnrollmentProvider>
  );
}

export default CobraBenefitsEnrollment;
